/**
 * InactivityTransferConfigSection — transferência automática quando o
 * atendente não responde (spec DOCS/superpowers/specs/2026-09-29-transferencia-por-inatividade-design.md).
 *
 * Vale só no INÍCIO do atendimento (D21, 02/10/2026): depois da primeira
 * resposta de uma pessoa a conversa nunca mais é transferida sozinha.
 * Se o cliente escreve e o responsável não responde em X minutos, a conversa
 * passa para outra pessoa (rodízio do canal, ou uma pessoa fixa), até o
 * limite de transferências. Ausente ou `enabled !== true` = desligado — nada
 * muda para quem não ligar.
 *
 * O backend valida as mesmas regras (`inactivity-transfer-validation.ts`);
 * aqui a tela só evita oferecer o que não vai funcionar.
 */

import React from 'react';
import {
  DEFAULT_CHANNEL_INACTIVITY_TRANSFER_CONFIG,
  type ChannelInactivityTransferConfig,
  type DistributionConfig,
  type UpdateChannelRequest,
} from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { useContextUsers } from '@/shared/hooks/useContextUsers';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select, type SelectOption } from '@/shared/components/ui/Select';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons';

interface InactivityTransferConfigSectionProps {
  channel: ChannelResponse;
  formData: Partial<UpdateChannelRequest>;
  onFieldChange: (field: keyof UpdateChannelRequest, value: unknown) => void;
}

const EMAIL_PROVIDER_ID = 'email-resend';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Campo numérico com o texto em estado local: dá para apagar e digitar à
 * vontade; só ao sair do campo o valor é ajustado ao intervalo e enviado —
 * e só se mudou (entrar e sair sem mexer não marca "alterações não salvas").
 */
const NumberField: React.FC<{
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  testId: string;
  onCommit: (value: number) => void;
}> = ({ id, label, value, min, max, testId, onCommit }) => {
  const [text, setText] = React.useState(String(value));
  React.useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = (): void => {
    const parsed = Number.parseInt(text, 10);
    const next = Number.isFinite(parsed) ? clamp(parsed, min, max) : value;
    setText(String(next));
    if (next !== value) onCommit(next);
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
        {label}
      </label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        data-testid={testId}
      />
    </div>
  );
};

const Hint: React.FC<{ testId?: string; tone?: 'info' | 'warning'; children: React.ReactNode }> = ({
  testId,
  tone = 'info',
  children,
}) => (
  <div
    className="p-3 rounded-lg border"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      borderColor: tone === 'warning' ? 'var(--theme-warning-color, var(--theme-card-border))' : 'var(--theme-card-border)',
    }}
    data-testid={testId}
  >
    <div className="flex items-start gap-2">
      <Icon
        name={tone === 'warning' ? 'alert-triangle' : 'info'}
        className="w-4 h-4 mt-0.5 flex-shrink-0"
        style={{ color: tone === 'warning' ? 'var(--theme-warning-color, var(--theme-card-muted))' : 'var(--theme-card-muted)' }}
      />
      <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
        {children}
      </p>
    </div>
  </div>
);

export const InactivityTransferConfigSection: React.FC<InactivityTransferConfigSectionProps> = ({
  channel,
  formData,
  onFieldChange,
}) => {
  const currentConfig: ChannelInactivityTransferConfig =
    formData.inactivityTransferConfig ?? channel.inactivityTransferConfig ?? DEFAULT_CHANNEL_INACTIVITY_TRANSFER_CONFIG;
  const assignment: DistributionConfig | undefined =
    (formData.assignmentConfig ?? channel.assignmentConfig) as DistributionConfig | undefined;

  const isEmailChannel = channel.providerId === EMAIL_PROVIDER_ID;
  const isEnabled = currentConfig.enabled && !isEmailChannel;
  const isFixed = currentConfig.target === 'fixed_operator';
  const distributionUsable = assignment?.enabled === true && assignment.type !== 'fixed_operator';
  const expiration = formData.expirationConfig ?? channel.expirationConfig;

  // Só busca com a opção ligada. O endpoint de usuários do canal já devolve só
  // vínculos ATIVOS (não traz `status`) — a contagem olha só o papel.
  const { users: contextUsers, isLoading: contextUsersLoading } = useContextUsers(
    isEnabled ? { kind: 'channel', id: channel.id } : undefined,
  );
  const attendantCount = contextUsers.filter((u) => u.role === 'attendant').length;

  // D12: a pessoa fixa precisa estar vinculada ao canal (qualquer papel).
  const memberOptions: SelectOption[] = contextUsers.map((u) => ({ value: u.userId, label: u.name ?? u.userId }));
  const fixedUserIsMember =
    !currentConfig.fixedOperatorUserId ||
    contextUsers.some((u) => u.userId.toLowerCase() === currentConfig.fixedOperatorUserId?.toLowerCase());

  // Sempre envia o objeto completo — o PUT genérico substitui o sub-objeto inteiro.
  const emit = (partial: Partial<ChannelInactivityTransferConfig>): void => {
    onFieldChange('inactivityTransferConfig', { ...currentConfig, ...partial });
  };

  const handleToggle = (checked: boolean): void => {
    // Ao ligar num canal sem rodízio utilizável, já começa em "pessoa fixa".
    if (checked && !distributionUsable && currentConfig.target === 'distribution') {
      emit({ enabled: true, target: 'fixed_operator' });
      return;
    }
    emit({ enabled: checked });
  };

  const targetOptions: SelectOption[] = [
    {
      value: 'distribution',
      label: distributionUsable
        ? 'Próxima pessoa pelo rodízio do canal'
        : 'Próxima pessoa pelo rodízio do canal (ligue a distribuição do canal)',
      disabled: !distributionUsable,
    },
    { value: 'fixed_operator', label: 'Uma pessoa fixa (ex.: gerente)' },
  ];

  if (isEmailChannel) {
    return (
      <Card data-testid="inactivity-transfer-section">
        <CardHeader className="pb-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <Icon name="clock" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
            <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
              Transferir quando o atendente não responde
            </h3>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <Hint testId="inactivity-transfer-email-unavailable">
            Não disponível para canais de e-mail.
          </Hint>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="inactivity-transfer-section">
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="clock" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
            Transferir quando o atendente não responde
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="inactivity-transfer-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Passar a conversa para outra pessoa se o cliente ficar sem a primeira resposta
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Vale só no início do atendimento: conta a partir da mensagem do cliente, até a primeira resposta de uma pessoa. Depois que alguém respondeu, a conversa não é mais transferida sozinha. Se a próxima pessoa também não responder no mesmo tempo, passa de novo, até o limite.
            </p>
          </div>
          <Switch
            id="inactivity-transfer-toggle"
            checked={isEnabled}
            onChange={handleToggle}
            data-testid="inactivity-transfer-toggle"
          />
        </div>

        {isEnabled && (
          <div className="space-y-4 pt-4 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NumberField
                id="inactivity-transfer-minutes"
                label="Tempo para a primeira resposta (minutos)"
                value={currentConfig.timeoutMinutes}
                min={1}
                max={1440}
                testId="inactivity-transfer-minutes"
                onCommit={(timeoutMinutes) => emit({ timeoutMinutes })}
              />
              <NumberField
                id="inactivity-transfer-max"
                label="Máximo de transferências por espera"
                value={currentConfig.maxTransfers}
                min={1}
                max={10}
                testId="inactivity-transfer-max-transfers"
                onCommit={(maxTransfers) => emit({ maxTransfers })}
              />
            </div>

            <Select
              label="Para quem vai"
              options={targetOptions}
              value={currentConfig.target}
              onChange={(value) => emit({ target: value === 'fixed_operator' ? 'fixed_operator' : 'distribution' })}
              data-testid="inactivity-transfer-target"
            />

            {!isFixed && !distributionUsable && (
              <Hint testId="inactivity-transfer-warning-distribution-off" tone="warning">
                A distribuição automática deste canal está desligada ou entrega sempre para a mesma pessoa. Ligue o rodízio na Configuração de Distribuição, ou escolha uma pessoa fixa.
              </Hint>
            )}

            {!isFixed && distributionUsable && !contextUsersLoading && attendantCount <= 1 && (
              <Hint testId="inactivity-transfer-warning-single-attendant" tone="warning">
                {attendantCount === 1
                  ? 'Este canal tem só 1 atendente — o rodízio não tem para quem passar.'
                  : 'Este canal não tem atendente — o rodízio não tem para quem passar.'}
              </Hint>
            )}

            {!isFixed && distributionUsable && attendantCount === 2 && (
              <Hint testId="inactivity-transfer-info-two-attendants">
                Com 2 atendentes, a conversa passa uma vez só: quem deixou o cliente sem resposta não recebe de volta na mesma espera.
              </Hint>
            )}

            {isFixed && (
              <>
                <Select
                  label="Pessoa que recebe"
                  description="Precisa estar vinculada a este canal (pode ser só visualização)."
                  placeholder="Escolha uma pessoa"
                  options={memberOptions}
                  value={currentConfig.fixedOperatorUserId ?? ''}
                  onChange={(value) => emit({ fixedOperatorUserId: value })}
                  data-testid="inactivity-transfer-fixed-user"
                />
                {!contextUsersLoading && !fixedUserIsMember && (
                  <Hint testId="inactivity-transfer-warning-fixed-user-gone" tone="warning">
                    A pessoa escolhida não está mais vinculada a este canal — as conversas sem resposta não serão transferidas. Escolha outra pessoa.
                  </Hint>
                )}
              </>
            )}

            <Switch
              label="Contar só no horário comercial"
              description="Fora do expediente o tempo pausa e volta a contar quando a empresa abre. O horário é o de Configurações → Empresa → Horário de atendimento."
              checked={currentConfig.businessHoursOnly}
              onChange={(checked) => emit({ businessHoursOnly: checked })}
              data-testid="inactivity-transfer-business-hours"
            />

            {currentConfig.businessHoursOnly && expiration?.enabled === true && (
              <Hint testId="inactivity-transfer-warning-expiration" tone="warning">
                A expiração do atendimento deste canal ({expiration.expirationMinutes} min) conta o relógio corrido, inclusive fora do expediente. Uma conversa que chega perto do fim do dia pode expirar antes de ser transferida.
              </Hint>
            )}

            <Switch
              label="Levar o lead junto"
              description="O lead aberto do cliente acompanha a conversa, se era de quem deixou o cliente sem resposta e se o novo responsável atende naquele funil. A pontuação e a comissão de uma venda vão para quem é dono do lead na hora do ganho."
              checked={currentConfig.transferLinkedLead}
              onChange={(checked) => emit({ transferLinkedLead: checked })}
              data-testid="inactivity-transfer-linked-lead"
            />

            <Hint>
              Conta como resposta uma mensagem de pessoa, pela tela ou pelo celular do canal. Não contam: nota interna, mensagens automáticas, a IA e só clicar em "Iniciar atendimento". Quem perde a conversa é avisado. Se o seu WhatsApp Business tem saudação automática, ela conta como a primeira resposta e a conversa nunca é transferida — prefira a saudação feita por automação no sistema.
            </Hint>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

InactivityTransferConfigSection.displayName = 'InactivityTransferConfigSection';
